<script setup lang="ts">
import type { UnprefixedOrganizationNumber } from '#shared/organization-number'
import {
  isValidOrganizationNumber,

  withoutPrefix,
} from '#shared/organization-number'
import { faPaperPlane } from '@fortawesome/pro-duotone-svg-icons'
import { FetchError } from 'ofetch'

definePageMeta({
  layout: false,
  colorMode: 'light',
})

const stepId = ref<RegisterUserStepId>('search')

const search = ref('')

const selectedOrganization = ref<BrregEnhet>()

const error = ref<string>()

const size = useRouteQuery('size', undefined, {
  mode: 'replace',
  transform: (it) => {
    const n = Number(it)
    return Number.isNaN(n) ? undefined : n
  },
})

const orgNo = useRouteQuery<UnprefixedOrganizationNumber | undefined>('orgNo')

const { data: brregInfo, isFetching, suspense: waitForBrregUnit } = useBrregUnitQuery(orgNo)

const { data: cmsData, suspense: waitForCmsData } = useRegisterUserCmsData()

if (isFetching.value) {
  await Promise.all([
    waitForCmsData(),
    waitForBrregUnit(),
  ])
}
else {
  await waitForCmsData()
}

const step = computed(() => cmsData.value?.components?.steps?.find(s => s.id === stepId.value))

const stepOrder = computed<RegisterUserStepId[] | undefined>(() => {
  if (!selectedOrganization.value) {
    return undefined
  }

  if (isLegalEntity(selectedOrganization.value)) {
    // Legal entities are not allowed to change number of employees
    return [
      'search',
      'confirmation',
      'admin',
      'receipt',
    ]
  }

  return [
    'search',
    'confirmation',
    'size',
    'admin',
    'receipt',
  ]
})

watch(selectedOrganization, (value) => {
  if (isValidOrganizationNumber(value?.organisasjonsnummer)) {
    orgNo.value = withoutPrefix(value.organisasjonsnummer)
  }
  else {
    orgNo.value = undefined
  }
})

watch(brregInfo, (value) => {
  if (value) {
    const maxSize = (value.harRegistrertAntallAnsatte && value.antallAnsatte) || 4

    if (size.value && size.value > maxSize) {
      size.value = undefined
    }

    if (value.organisasjonsnummer !== selectedOrganization.value?.organisasjonsnummer) {
      selectedOrganization.value = value
    }
  }
}, {
  immediate: true,
})

const textVariables = reactive<Record<string, string>>({})

const isLoading = ref(false)

async function handleStepChange(event: Event) {
  const org = selectedOrganization.value
  const steps = stepOrder.value

  // The user can never change step without having selected an organization (or without having steps to complete)
  if (!org || !steps) {
    stepId.value = 'search'
    error.value = 'Du må velge en bedrift for å fortsette'
    return
  }

  const button = (event as SubmitEvent).submitter

  if (!button || !(button instanceof HTMLButtonElement) || button.name !== 'direction') {
    throw new Error('Form submitted by unexpected trigger')
  }

  error.value = undefined

  let newStep: RegisterUserStepId | undefined
  if (button.value === 'previous') {
    newStep = steps[steps.indexOf(stepId.value) - 1]
  }
  else if (button.value === 'next') {
    newStep = steps[steps.indexOf(stepId.value) + 1]

    // Only run validations when moving forward
    switch (stepId.value) {
      case 'size': {
        if (!size.value) {
          error.value = 'Du må oppgi antall jurister for å fortsette'
        }
        else if (org.harRegistrertAntallAnsatte && typeof org.antallAnsatte === 'number' && size.value > org.antallAnsatte) {
          error.value = `Antall jurister kan ikke være høyere enn totalt antall ansatte (${org.antallAnsatte})`
        }
        break
      }
      case 'admin': {
        const data = jsonFromForm(event)
        if (!data.firstName || !data.lastName || !data.email) {
          error.value = 'Du må fylle ut alle påkrevde felter for å fortsette'
          break
        }

        textVariables.email = String(data.email)

        try {
          const body = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            username: data.email,
            organizationNumber: org.organisasjonsnummer,
          }

          isLoading.value = true
          await $fetch.raw('/api/core/self-service/user/registration', {
            method: 'POST',
            body: isLegalEntity(selectedOrganization.value)
              ? body
              : {
                  ...body,
                  numberOfEmployees: size.value,
                },
          })
        }
        catch (e) {
          if (e instanceof FetchError) {
            if (e.status === 409) {
              error.value = 'user-exists'
            }
            else if (e.status === 403) {
              error.value = 'invalid-organization'
            }
            else {
              error.value = 'generic-error'
            }
          }
        }
        finally {
          isLoading.value = false
        }
        break
      }
    }
  }

  if (!error.value && newStep) {
    stepId.value = newStep
  }
}
</script>

<template>
  <NuxtLayout name="login">
    <template v-if="stepOrder" #header>
      <RegisterUserHeader :step-order="stepOrder" :current-step="stepId" />
    </template>

    <section class="full-width mb-2xl grid grid-cols-subgrid gap-y-xl pt-2xl pb-4xl transition-all duration-300">
      <div
        class="flex gap-xl col-span-4"
        :class="{
          'lg:col-start-3 2xl:col-span-6 2xl:col-start-4': stepId !== 'receipt',
          'lg:col-start-2 lg:col-span-6 2xl:col-start-3 2xl:col-span-8': stepId === 'receipt',
        }"
      >
        <Icon
          v-if="stepId === 'receipt'"
          :icon="faPaperPlane"
          class="hidden sm:block text-blurple-400 size-20"
        />

        <hgroup class="flex flex-col gap-xs">
          <h1 class="title-l" v-text="step?.title ?? `${stepId} (mangler tittel)`" />

          <CrystallizeRichText
            v-for="(block, i) in step?.description"
            :key="i"
            :block="block"
            class="text-lg"
            :variables="textVariables"
          />
        </hgroup>
      </div>

      <form class="flex flex-col gap-l col-span-4 lg:col-start-3 2xl:col-start-4" @submit.prevent="handleStepChange">
        <Callout v-if="error === 'user-exists'" variant="warning" title="Bruker eksisterer allerede">
          <span>
            En bruker med denne e-postadressen eksisterer allerede. Gå til

            <NuxtLink :to="{ name: 'login' }" class="underline hover:contrast-50">
              innloggingssiden
            </NuxtLink>

            for å logge inn.
          </span>
        </Callout>

        <Callout v-else-if="error === 'invalid-organization'" variant="warning" title="Registrering mislyktes">
          <span>
            Organisasjonen finnes allerede i vårt system. Ta kontakt med
            <NuxtLink :to="{ name: 'info-page', params: { slug: 'kundeservice' } }">kundeservice</NuxtLink>
            om du mener dette ikke stemmer.
          </span>
        </Callout>

        <Callout v-else-if="error === 'generic-error'" variant="error" title="Noe gikk galt">
          <span>
            En ukjent feil oppsto under registreringen. Vennligst prøv igjen senere eller ta kontakt med <NuxtLink
              class="text-base" :to="{ name: 'info-page', params: { slug: 'kundeservice' } }"
            >
              Kundeservice
            </NuxtLink> om feilen vedvarer.
          </span>
        </Callout>

        <Callout v-else-if="error" variant="warning" :title="error" />

        <div class="flex flex-col gap-2xl">
          <OrgSearch
            v-if="stepId === 'search'"
            v-model:search="search"
            v-model="selectedOrganization"
            class="w-full"
            :items="brregInfo ? [brregInfo] : []"
          />

          <dl v-else-if="stepId === 'confirmation'" class="flex flex-col gap-m">
            <DescriptionListItem title="Navn på bedrift">
              {{ selectedOrganization?.navn }}
            </DescriptionListItem>

            <DescriptionListItem title="Organisasjonsnummer">
              {{ selectedOrganization?.organisasjonsnummer }}
            </DescriptionListItem>

            <DescriptionListItem title="Næringskode">
              <div class="flex flex-col gap-3xs">
                {{ getPrimaryIndustryCode(selectedOrganization)?.kode }}

                <i class="text-base !leading-tight font-body">{{ getPrimaryIndustryCode(selectedOrganization)?.beskrivelse }}</i>
              </div>
            </DescriptionListItem>

            <DescriptionListItem v-if="selectedOrganization?.postadresse" title="Postadresse">
              <address class="not-italic" v-text="formatAddress(selectedOrganization?.postadresse)" />
            </DescriptionListItem>

            <DescriptionListItem v-else-if="selectedOrganization?.forretningsadresse" title="Forretningsadresse">
              <address class="not-italic" v-text="formatAddress(selectedOrganization?.forretningsadresse)" />
            </DescriptionListItem>
          </dl>

          <FormInput
            v-else-if="stepId === 'size'"
            id="size"
            v-model="size"
            type="number"
            placeholder="Skriv antall..."
            label="Oppgi antall jurister"
            :disabled="isLegalEntity(selectedOrganization)"
            min="1"
            :max="selectedOrganization?.antallAnsatte"
          />

          <div v-else-if="stepId === 'admin'" class="flex flex-col gap-m">
            <h2 class="title-s">
              Kontaktinformasjon
            </h2>

            <FormInput id="firstName" type="text" placeholder="Ditt fornavn..." label="Fornavn" required />
            <FormInput id="lastName" type="text" placeholder="Ditt etternavn..." label="Etternavn" required />
            <FormInput id="email" type="text" placeholder="Din e-postadresse..." label="E-postadresse" required />
          </div>

          <div v-if="step?.nextLabel || step?.previousLabel" class="flex gap-xl">
            <Button v-if="step?.nextLabel" :disabled="isLoading" variant="primary" color="blurple" type="submit" name="direction" value="next">
              {{ step.nextLabel }}

              <Spinner v-if="isLoading" color="carbon" size="small" class="!size-5" />
            </Button>

            <button v-if="step?.previousLabel" class="underline hover:contrast-50" type="submit" name="direction" value="previous">
              {{ step.previousLabel }}
            </button>
          </div>
        </div>
      </form>
    </section>
  </NuxtLayout>
</template>
